/**
 * TURNING BYTES INTO A FILE ON SOMEBODY'S DISK.
 *
 * The bytes come from the transport (`requestText` for a CSV, `requestBlob` for an
 * attachment), so the cookie, the error mapping and the 401 hook are the ones every other
 * call has - which is why these functions take the CONTENT rather than a URL. A bare
 * `<a href="/api/exports/:id/file" download>` would have been shorter and is wrong in three
 * ways that all bite here: a 403 renders the JSON error envelope as a page instead of
 * raising, the screen that asked cannot report the refusal, and under the fixtures build
 * there is no server behind the link at all.
 */
export function downloadCsv(filename: string, text: string): void {
  downloadBlob(filename, new Blob([text], { type: 'text/csv;charset=utf-8' }));
}

/**
 * THE SAME MECHANISM, ONE STEP EARLIER (`file-attachments.md` D5).
 *
 * `downloadCsv` was this function with a `Blob` constructor welded to its front. An
 * attachment is already bytes when it reaches the browser - `transport.requestBlob` hands
 * back what the server sent, sniffed type and all - so wrapping it in a second Blob with a
 * CSV mime type would relabel a PDF as a spreadsheet on the way to the disk.
 *
 * Every word of the paragraph above still applies: the bytes came through the transport, so
 * the cookie, the error mapping and the 401 hook are the ones every other call has, and
 * `revokeObjectURL` is not optional bookkeeping - at a 25 MB cap, five un-revoked downloads
 * is 125 MB held for the life of the document.
 */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  /**
   * Appended before the click and removed after: Firefox ignores a click on an anchor that
   * is not in the document, and leaving it there would litter the body with one dead node
   * per download.
   */
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
